//BasemapGallery.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as MainMap from "../MainMap";
import * as MainToolbar from "../Menus/MainToolbar";
import * as ParcelSymbol from "../Util/ParcelSymbol";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Util from "../Util/Util";
import * as WidgetHeading from "./WidgetHeading";
import * as Widgets from "./Widgets";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Icon, Paper, Slider, TextField, Typography, Zoom } from "@mui/material";

import React from "react";

import Basemap from "@arcgis/core/Basemap";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import ImageryTileLayer from "@arcgis/core/layers/ImageryTileLayer";
import Map from "@arcgis/core/Map";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
import TileLayer from "@arcgis/core/layers/TileLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";

const toolName: string = "Basemap Gallery";
let toolDiv: string; //Container div of the widget.

let bmWidget: __esri.BasemapGallery; // Basemap gallery widget.
let basemapName: string = "";
let basemapNames: string[] = []; //Just a list of the basemap id's
let bmSliderValue: number = 0;
let opacityValue: number = 0;
let basemapURL: string = "";

let bmWidget_choose: __esri.BasemapGallery = null; // Basemap gallery widget.
let map_choose: __esri.Map;
let mapView_choose: __esri.MapView;
let addingBasemap = false;

//Initialize basemap gallery.
function initBasemapGallery() {
   if (basemapNames.length === 0) {
      basemapNames = [];

      for (const bm of MainMap.basemaps) {
         basemapNames.InsertAt(0, bm.id);
      }
   }
}

//Null the widget.
export function destroy() {
   if (bmWidget) {
      bmWidget = null;
   }
}

//Set slider values.
function setSliderValues() {
   if (basemapNames) {
      for (let i: number = 0; i < basemapNames.length; i++) {
         if (basemapNames[i] === MainMap.map.basemap.id) {
            bmSliderValue = i;

            const j: number = MainMap.basemaps.length - i - 1; //Get index of active basemap.
            opacityValue = MainMap.basemaps[j].baseLayers.getItemAt(0).opacity;
            break;
         }
      }
      basemapName = basemapNames[bmSliderValue];
   }
}

//Set basemap for slider in gallery.
function setSliderBasemap() {
   const basemap = getBaseMapOrBaseLayer(basemapName) as __esri.Basemap;

   if (basemap) {
      const baseLayer = getBaseMapOrBaseLayer(basemapName, true) as __esri.Layer;
      if (baseLayer) {
         opacityValue = baseLayer.opacity;
         baseLayer.opacity = opacityValue;
      }

      //Util.errMsg("BasemapGallery: Set slider change basemap:", basemap);
      Widgets.setBasemap(basemap);
   }
}

//Set basemap opacity.
function setOpacity() {
   const baseLayer = getBaseMapOrBaseLayer(basemapNames[bmSliderValue], true) as __esri.Layer;
   if (baseLayer) {
      baseLayer.opacity = opacityValue;
   }
}

//Get basemap or base layer.
function getBaseMapOrBaseLayer(name: string, baseLayer: boolean = false) {
   for (const basemap of MainMap.basemaps) {
      if (basemap.id === name) {
         if (baseLayer) {
            return basemap.baseLayers.getItemAt(0);
         } else {
            return basemap;
         }
      }
   }
   return null;
}

interface iProps {
   fadeIn: boolean;
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of BasemapGallery Render");

   //Define state.
   const [visible, setVisible] = React.useState(false); //Set open or close dialog method.

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did update.
   React.useEffect(() => {
      if (DrawerMenu.curToolName === "Basemap Gallery") {

         //Load the ESRI api widget.
         import("@arcgis/core/widgets/BasemapGallery").then(({ default: BasemapGalleryWidget }) => {

            Util.logMsg("Basemap gallery did update");

            if (!bmWidget && props.fadeIn) {

               Util.logMsg("Creating map and map view for secondary Basemap Gallery widget");
               map_choose = new Map({ basemap: "streets-vector" });
               mapView_choose = new MapView({ container: "mapView-choose", map: map_choose });

               Util.logMsg("Creating Basemap Gallery widget");
               initBasemapGallery(); //Executes first time only in above code.
               setSliderValues();

               //Create widget.
               bmWidget = new BasemapGalleryWidget({
                  container: toolDiv,
                  view: MainMap.activeView,
                  source: MainMap.basemaps,
               });

               refresh();

               //Basemap widget changed map click event.
               bmWidget.watch("activeBasemap", function (newValue: __esri.Basemap, oldValue: __esri.Basemap) {
                  if (addingBasemap) {
                     addingBasemap = false;
                     return; //Quit if adding a new basemap.
                  }

                  basemapName = newValue.id;
                  ParcelSymbol.setParcelSymbol(basemapName);

                  for (let i: number = 0; i < basemapNames.length; i++) {
                     if (basemapNames[i] === basemapName) {
                        bmSliderValue = i;

                        const baseLayer = getBaseMapOrBaseLayer(basemapNames[i], true) as __esri.Layer;
                        if (baseLayer) {
                           opacityValue = baseLayer.opacity;
                        }
                        break;
                     }
                  }

                  //Don't allow both toogle basemap basemaps to be the same.
                  if (MainMap.map.basemap.id === basemapName) {
                     //Util.errMsg("BasemapGallery - Basemap changed, so setting the next (top thumbnail) basemap");
                     Widgets.setNextBasemap(oldValue);
                  }

                  MainToolbar.addOrRemove3D();
                  MainToolbar.render();

                  refresh();
               });

            }

         }).catch((error: Error) => {
            Util.errMsg('An error occurred while loading the Basemap Gallery module:', error);
         });
      } //end tool name matched.
   });

   //Slider changed event.
   function sliderChanged(event: Event, value: number) {
      if (MainMap.activeView.updating) {
         return; //Quit if updating the view.
      }
      if (value !== bmSliderValue) {
         bmSliderValue = value;
         basemapName = basemapNames[value];
         setSliderBasemap();
         refresh();
      }
   }

   //Opacity changed event.
   function opacityChanged(event: Event, value: number) {
      if (value !== opacityValue) {
         opacityValue = value;
         setOpacity();
         refresh();
      }
   }

   //Animation is complete.
   function fadeInCompleted() {
      if (bmWidget) {
         bmWidget.visible = true;
      }
   }
   function fadeOutCompleted() {
      if (bmWidget) {
         bmWidget.visible = false;
      }
   }

   //Open dialog.
   function handleOpen() {
      setVisible(true);

      //Create the secondary basemap widget to choose a basemap to add.
      import("@arcgis/core/widgets/BasemapGallery").then(({ default: BasemapGalleryWidget }) => {

         //Create widget.
         bmWidget_choose = new BasemapGalleryWidget({
            container: toolDiv + "_choose",
            view: mapView_choose,
            //   source: { // autocasts to PortalBasemapsSource
            //      portal: "https://www.arcgis.com"
            //   }
         });

         //Basemap dialog choose list widget clicked on thumbnail event.
         bmWidget_choose.watch("activeBasemap", function (newValue: __esri.Basemap, oldValue: __esri.Basemap) {
            if (addingBasemap) {
               addingBasemap = false;
               //Util.errMsg("BasemapGallery Add Dialog - Exiting because adding new basemap mode is on:");
               return; //Quit if adding a new basemap.
            }

            const basemap = newValue.clone();
            basemap.spatialReference = newValue.spatialReference.clone(); //Force cloneing.
            basemap.thumbnailUrl = newValue.thumbnailUrl; //Force thumbnail.
            basemap.id = basemap.title;

            //Util.errMsg("BasemapGallery Add Dialog - Basemap widget map changed:", basemap);

            if (!basemapNames.Contains(basemap.title)) {
               addingBasemap = true;
               MainMap.basemaps.InsertAt(0, basemap);
               basemapNames.push(basemap.title);
               bmWidget.source = MainMap.basemaps as any;
            }

            Widgets.setBasemap(basemap);
         });

      }).catch((error: Error) => {
         Util.errMsg('An error occurred while loading the Basemap Gallery module:', error);
      });
   }

   //Close dialog.
   function handleClose() {
      setVisible(false);
      basemapURL = "";
      bmWidget_choose = null;
   }

   //URL of basemap to add.
   function basemapURL_changed(event: React.ChangeEvent<HTMLInputElement>) {
      basemapURL = event.target.value.replace(/ /g, "").trim();
   }

   //Add URL specified basemap to map.
   function handleAdd() {
      let baseLayer: __esri.Layer;
      if (!basemapURL) {
         return;
      }

      const sa: string[] = basemapURL.split("/");
      if (sa.length < 4) {
         SnackBar.show("Invalid map service url", 3);
         return;
      }

      const serviceType = sa[sa.length - 1].toLowerCase();
      const id = sa[sa.length - 2];
      const title = id.replace(/_/g, " ");

      if (basemapNames.Contains(title)) {
         //SnackBar.show("Basemap already exists in gallery", 3);
         return;
      }

      const props: __esri.FeatureLayerProperties = { url: basemapURL, legendEnabled: true };

      // Tile Layer or Map Image Layer.
      if (serviceType === "mapserver") {

         //Try tile layer.
         baseLayer = new TileLayer(props);
         baseLayer.load().then(function () {
            addLayer("TileLayer", title, baseLayer);

         }).catch(function (error: Error) {

            //Try map image layer.
            baseLayer = new MapImageLayer(props);
            baseLayer.load().then(function () {
               addLayer("MapImageLayer", title, baseLayer);

            }).catch(function (error: Error) {
               Util.errMsg("MapServer base layer failed to load as either a tile layer or map image layer.", error);
               SnackBar.show(error.message, 3);
            });
         });

         // Imagery Tile Layer or Imagery Layer.
      } else if (serviceType === "imageserver") {
         //Try imagery tile layer.
         baseLayer = new ImageryTileLayer(props as __esri.ImageryTileLayerProperties);
         baseLayer.load().then(function () {
            addLayer("ImageryTileLayer", title, baseLayer);

         }).catch(function (error: Error) {

            //Try imagery layer.
            baseLayer = new ImageryLayer(props as __esri.ImageryLayerProperties);
            baseLayer.load().then(function () {
               addLayer("ImageryLayer", title, baseLayer);

            }).catch(function (error: Error) {
               Util.errMsg("ImageryLayer base layer failed to load as either an imagery tile layer or imagery layer.", error);
               SnackBar.show(error.message, 3);
            });
         });

         // Vector Tile Layer.
      } else if (serviceType === "vectortileserver") {
         baseLayer = new VectorTileLayer(props);
         baseLayer.load().then(function () {
            addLayer("VectorTileLayer", title, baseLayer);

         }).catch(function (error: Error) {
            Util.errMsg("VectorTileLayer base layer failed to load.", error);
            SnackBar.show(error.message, 3);
         });

      } else {
         SnackBar.show("Unsupported map service type: " + serviceType, 3);
         Util.errMsg("Unsupported map service type: " + serviceType);
      }
   }

   //Add a basemap from specified URL.
   function addLayer(serviceType: string, title: string, baseLayer: __esri.Layer) {
      //Util.errMsg("BasemapGallery - Adding " + serviceType + " Title: " + title);

      const basemap = new Basemap({
         id: title,
         title: title,
         baseLayers: [baseLayer],
         //thumbnailUrl: bm.thumbnail,
      });

      addingBasemap = true;
      MainMap.basemaps.InsertAt(0, basemap);
      basemapNames.push(title);
      bmWidget.source = MainMap.basemaps as any;

      Widgets.setBasemap(basemap);
   }

   //Close this tool.
   function hide() {
      DrawerMenu.closeTool();
   }

   //Styles.
   const styles: Default.iStyles = {
      appWidgetDiv: { position: 'absolute', top: 0, left: 0, zIndex: 3, minWidth: Settings.leftPanelWidth, overflow: 'auto' },
      slider: { width: 240 },
      gallery: { backgroundColor: Settings.paperColor, marginTop: 5 },
      gallery_choose: { backgroundColor: Settings.paperColor, marginTop: 3, height: 320 },
      gallery_mapview: { position: 'absolute', top: 0, left: 0, zIndex: 0, height: 5, width: 5 },
   };

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   toolDiv = "basemap-gallery-id-" + MainMap.view2D.toString();

   return (
      <Zoom in={props.fadeIn} timeout={500} onEntered={() => fadeInCompleted()} onExited={() => fadeOutCompleted()}>

         <div id="Basemap_Gallery_Widget" style={styles.appWidgetDiv}>
            <Paper>
               <WidgetHeading.Render title={toolName} onClose={hide} />

               <div id={scrollDivID} style={Default.styles.appToolDiv}>
                  <div className="app-flex-column-start" style={{ padding: 3 }}>

                     <Paper className="app-flex-column-start" style={{ paddingTop: 4, paddingBottom: 10, paddingLeft: 20, paddingRight: 20, backgroundColor: Settings.leftPanelLabelColor }}>

                        <div id="mapView-choose" style={styles.gallery_mapview}></div>

                        <Typography id="basemap-label" variant="body2">{basemapName}</Typography>

                        <Slider style={styles.slider}
                           value={bmSliderValue}
                           aria-labelledby="basemap-label"
                           valueLabelDisplay="off"
                           marks
                           min={0}
                           max={MainMap.basemaps.length - 1}
                           step={1}
                           onChange={sliderChanged}
                        />

                        <Typography id="opacity-label" variant="body2">Opacity</Typography>

                        <Slider style={styles.slider}
                           value={opacityValue}
                           aria-labelledby="opacity-label"
                           valueLabelDisplay="off"
                           marks
                           min={0}
                           max={1}
                           step={0.05}
                           onChange={opacityChanged}
                        />

                        {/*<Button variant="outlined" size="medium" color="info" onClick={handleOpen} title="Add basemap" aria-label="Add">*/}
                        {/*   <Icon color="action">add</Icon>*/}
                        {/*   <span style={{ marginLeft: 6 }}></span>*/}
                        {/*   Add*/}
                        {/*</Button>*/}
                     </Paper>

                     <div id={toolDiv} key={toolDiv} style={styles.gallery}>
                        {/*CUSTOM! Poway*/}
                        <div style={{ color: 'white', cursor: 'default' }}>Basemap Gallery</div>
                     </div>
                  </div>
               </div>
            </Paper>

            <Dialog
               maxWidth="sm"
               role="dialog"
               aria-modal="true"
               id="add-basemap-dialog"
               open={visible}
               onClose={handleClose}
               aria-label="Add basemap"
               aria-labelledby="add-basemap-title"
               aria-describedby="add-basemap-description"
               slotProps={{
                  backdrop: {
                     sx: { backgroundColor: "rgba(0, 0, 0, 0.15)" }, // Adjust transparency here
                  },
               }}
            >
               <DialogTitle id="add-basemap-title" style={{ paddingBottom: 0, paddingTop: 9 }}>
                  Add Basemap
               </DialogTitle>

               <DialogContent id="add-basemap-description">
                  <div id={toolDiv + "_choose"} style={styles.gallery_choose}>Basemaps</div>
                  <br />

                  <div style={styles.col1Container}>
                     <div>
                        <Typography variant="body1"><b>Add using a case sensitive web address (url)</b></Typography>
                        <Typography variant="body1">Example: https://basemaps.arcgis.com/arcgis/rest/services/</Typography>
                        <Typography variant="body1">&nbsp;&nbsp;World_Basemap_v2/VectorTileServer</Typography>
                     </div>
                  </div>

                  <TextField style={{ marginTop: 20 }}
                     id="basemap-url"
                     label="https://"
                     defaultValue=""
                     margin="dense"
                     autoFocus={false}
                     fullWidth
                     onChange={basemapURL_changed}
                  />
               </DialogContent>

               <DialogActions>
                  <Button onClick={handleAdd} color="info">
                     Add to map
                  </Button>
                  <Button onClick={handleClose} color="info">
                     Close
                  </Button>
               </DialogActions>
            </Dialog>
         </div>

      </Zoom>
   ); //end return.

} //end function.
